import * as React from "react";
import "styled-components/macro";
import { connect } from "react-redux";
import classnames from "classnames";
import Space from "styled-space";
import { Flex } from "@rebass/grid";
import {
  Button,
  Classes,
  InputGroup,
  ButtonGroup,
  Intent,
  Text,
} from "@blueprintjs/core";
import * as R from "ramda";

import { ThunkDispatch } from "../../../types/redux";
import {
  ClientAttribute,
  Dimension,
  ClientAttributeInstance as ClientAttributeInstanceModal,
} from "../../../types/models";

import {
  clientAttributeActions,
  createClientAttributeData,
} from "src/store/modules/client_attributes";
import { ClientAttributeInstance } from "./ClientAttributeInstance";
import Toaster from "src/components/molecules/Toaster";

export const DEFAULT_CLIENT_ATTRIBUTE_NAME =
  "!__DEFAULT_CLIENT_ATTRIBUTE_NAME__";
export const DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME =
  "!__DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME__";

const columnCSS = {
  borderRadius: "3px",
  flexDirection: "column",
  minWidth: "200px",
  "&:last-child": {
    paddingBottom: "20px",
  },
};

interface IProps {
  dispatch: ThunkDispatch;
  attribute: ClientAttribute;
  validateName: (name: string) => boolean;
  isEditingView: boolean;
  selectedInstance?: ClientAttributeInstanceModal;
  onViewInstanceSidebar?: (
    attributeInstance: ClientAttributeInstanceModal,
    attribute: Dimension
  ) => void;
  onDeleteInstanceMappings?: (attributeInstanceId: number) => void;
  clientId: number;
}

export class ClientAttributeColumn extends React.Component<IProps, any> {
  constructor(props: any) {
    super(props);

    this.isValidAttributeInstanceName =
      this.isValidAttributeInstanceName.bind(this);

    this.state = {
      attributeName: "",
      errorMsg: "",
      isEditingAttribute:
        this.props.attribute.Name === DEFAULT_CLIENT_ATTRIBUTE_NAME,
      isSaving: false,
      isDeleting: false,
      value: this.props.attribute.Name,
      clientAttributeInstances: [],
    };
  }

  public componentDidMount() {
    if (this.props.attribute.instances) {
      this.setState({ clientAttributeInstances: this.props.attribute.instances });
    }
  }

  public render() {
    const btnClasses = classnames(Classes.LARGE, Classes.MINIMAL);
    const { attribute, selectedInstance } = this.props;
    const { isEditingAttribute } = this.state;
    const isAddingAttributeInstance = (
      this.state.clientAttributeInstances ?? []
    ).some(
      (ai: ClientAttributeInstanceModal) =>
        DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME === ai.Name
    );

    return (
      <Flex px={3} pt={3} css={columnCSS}>
        <Space mb={3}>
          <div
            style={{
              boxShadow:
                "rgba(16, 22, 26, 0.1) 0px 0px 0px 1px, rgba(16, 22, 26, 0) 0px 0px 0px, rgba(16, 22, 26, 0.2) 0px 1px 1px",
              borderRadius: "3px",
            }}
          >
            <Flex justifyContent={"center"}>
              {isEditingAttribute ? (
                <this.AttributeEditing />
              ) : (
                <Flex
                  justifyContent={"space-between"}
                  width={1}
                  css={{ padding: "5px 16px", alignItems: "center" }}
                >
                  <Text className="bp4-text-muted bp4-text-large">
                    {attribute.Name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Button
              text={"Add Instance"}
              rightIcon="plus"
              className={btnClasses}
              onClick={this.createInstance}
              disabled={isAddingAttributeInstance || isEditingAttribute}
              fill
            />
          </div>
          {(this.state.clientAttributeInstances ?? []).map(
            (instance: ClientAttributeInstanceModal, i: any) => (
              <ClientAttributeInstance
                key={`${instance.id}-${i}`}
                attributeInstance={instance}
                attribute={attribute}
                dispatch={this.props.dispatch}
                validateName={this.isValidAttributeInstanceName}
                clientId={this.props.clientId}
                addNewAttrInstance={(instance: ClientAttributeInstanceModal) => {
                  const newInstances = [
                    ...this.state.clientAttributeInstances.filter(
                      (i: ClientAttributeInstanceModal) =>
                        i.Name !== DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME
                    ),
                    instance,
                  ];
                  this.setState({ clientAttributeInstances: newInstances });
                }}
                removeAttrInstance={() => {
                  const newInstances = [
                    ...this.state.clientAttributeInstances.filter(
                      (i: ClientAttributeInstanceModal) =>
                        i.Name !== DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME
                    ),
                  ];
                  this.setState({ clientAttributeInstances: newInstances });
                }}
                isHighlighted={
                  selectedInstance !== undefined &&
                  instance.id === selectedInstance.id
                }
              />
            )
          )}
          <div style={{ height: "20px", padding: "20px" }} />
        </Space>
      </Flex>
    );
  }

  private AttributeEditing: React.FC = () => {
    return (
      <form onSubmit={this.handleAttributeEdit}>
        <Flex>
          <InputGroup
            type="text"
            onChange={this.handleAttributeChange}
            placeholder="Attribute Name"
            required={true}
            autoFocus={true}
            defaultValue={
              this.props.attribute.Name !== DEFAULT_CLIENT_ATTRIBUTE_NAME
                ? this.props.attribute.Name
                : ""
            }
          />
          <ButtonGroup minimal={true}>
            {!this.props.isEditingView && (
              <Button
                icon="trash"
                loading={this.state.isDeleting}
                onClick={this.handleAttributeDelete}
                intent={Intent.DANGER}
              />
            )}
            <Button icon="tick" type="submit" loading={this.state.isSaving} />
          </ButtonGroup>
        </Flex>
        <Text className={Classes.TEXT_SMALL} style={{color: 'red', padding: '6px 12px 0'}}>{this.state.errorMsg}</Text>
      </form>
    );
  };

  private handleAttributeEdit = (event: any) => {
    event.preventDefault();
    if (this.props.validateName(this.state.attributeName)) {
      this.setState({ isSaving: true });
      this.props
        .dispatch(
          createClientAttributeData(
            this.props.clientId,
            this.state.attributeName || this.state.value
          )
        )
        .then(() => {
          this.setState({ isEditingAttribute: false, isSaving: false });
          Toaster.show({
            message: "Attribute created successfully",
            intent: Intent.SUCCESS,
          });
        })
        .catch(() => {
          this.setState({ isSaving: false });
          Toaster.show({
            message: "Error creating attribute",
            intent: Intent.DANGER,
          });
        });
    } else {
      this.setState({ errorMsg: "An Attribute already exists with this name." });
    }
  };

  private handleAttributeChange = (event: any) => {
    this.setState({ attributeName: R.trim(event.target.value) });
  };

  private handleAttributeDelete = () => {
    this.setState({ isEditingAttribute: false });
    this.props.dispatch(clientAttributeActions.clearUnsavedClientAttributes());
  };

  private isValidAttributeInstanceName = (newName: string): boolean => {
    return !(this.state.clientAttributeInstances ?? []).some(
      (ai: ClientAttributeInstanceModal) => {
        return (
          R.toLower(ai.Name || DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME) ===
          R.toLower(newName)
        );
      }
    );
  };

  private createInstance = () => {
    this.setState({
      clientAttributeInstances: [
        ...this.state.clientAttributeInstances,
        { Name: DEFAULT_CLIENT_ATTRIBUTE_INSTANCE_NAME },
      ],
    });
  };
}

export default connect()(ClientAttributeColumn);
